import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listAllProceduri } from '../../services/proceduri';
import { listStaffUsers } from '../../services/auth';
import { listClienti } from '../../services/clienti';
import { listTasks, createTask, updateTask, deleteTask, uploadTaskFile } from '../../services/tasks';
import { useAuth } from '../../hooks/useAuth';
import TaskModal from '../../components/Shared/TaskModal';
import '../../pages/Clienti/ClientiPage.css';
import './CalendarPage.css';

/* ── Constants ── */
const MONTHS_RO  = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];
const DAYS_RO    = ['Lun','Mar','Mie','Joi','Vin','Sâm','Dum'];
const STATUS_OPTS = [
  { value: 'neinceput',  label: 'Neinceput' },
  { value: 'in_progres', label: 'In progres' },
  { value: 'terminat',   label: 'Terminat' },
];

/* ── Helpers ── */
function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function todayISO() { return toISO(new Date()); }

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const offset = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d ? `${d}.${m}.${y}` : iso;
}

/* ── Event builder (litigii procedures) ── */
function buildProcEvents(proceduri) {
  const byDate = {};
  const add = (date, ev) => {
    if (!date) return;
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(ev);
  };
  for (const proc of proceduri) {
    const d = proc.data || {};
    const clientName = proc.client_name || '';
    const denumire   = d.denumire || '';
    for (const s of (d.sarcini || [])) {
      if (s.done) continue;
      const base = { text: s.text || '(fara descriere)', clientName, denumire, asignatLa: s.asignatLa || [], procId: proc.id, _isProc: true };
      if (s.scadentaDosar)   add(s.scadentaDosar,   { ...base, type: 'sarcina_dosar' });
      if (s.scadentaInterna && s.scadentaInterna !== s.scadentaDosar)
                              add(s.scadentaInterna, { ...base, type: 'sarcina_interna' });
    }
    for (const t of (d.termene || [])) {
      if (!t.data) continue;
      add(t.data, { type: 'termen', text: t.obiectTermen || 'Termen judecata', nota: t.nota || '', clientName, denumire, _isProc: true });
    }
    const sync = d.sedinte_sync;
    if (sync && Array.isArray(sync.sedinte)) {
      for (const s of sync.sedinte) {
        if (!s.data) continue;
        add(s.data, { type: 'sedinta', text: sync.institutie || 'Sedinta', solutie: s.solutie || '', ora: s.ora || '', clientName, denumire, _isProc: true });
      }
    }
  }
  return byDate;
}

/* ── Merge tasks into byDate map ── */
function mergeTaskEvents(byDate, tasks, clientiMap, proceduriMap) {
  const result = { ...byDate };
  const push = (dateKey, ev) => {
    if (!dateKey) return;
    (result[dateKey] || (result[dateKey] = [])).push(ev);
  };
  for (const t of tasks) {
    const clientNames = (t.clienti_ids || []).map(id => clientiMap[id]).filter(Boolean);
    const procNames   = (t.proceduri_ids || []).map(id => proceduriMap[id]).filter(Boolean);
    const base = {
      type:       'task',
      status:     t.status || 'neinceput',
      text:         t.nume,
      asignatLa:    t.asignat_la_names || [],
      clientName:   clientNames.join(', '),
      procName:     procNames.join(', '),
      descriere:    t.descriere || '',
      termenLimita: t.termen_limita || '',
      subtaskuri:   t.subtaskuri || [],
      taskId:       t.id,
      _isTask:      true,
    };
    const data = t.data || '';
    const term = t.termen_limita || '';

    // Planned entry on "Data" (unchanged). Skip if it coincides with the deadline.
    if (data && (!term || data !== term)) {
      push(data, { ...base });
    }
    // Deadline entry on "Termen limita" — red until the task is marked "Terminat".
    if (term) {
      push(term, { ...base, _isTaskDeadline: true });
    }
  }
  return result;
}

/* ── Pill color class ── */
function pillClass(ev) {
  if (ev._isTask) {
    if (ev.status === 'terminat')   return 'cal-pill cal-pill--green';
    if (ev._isTaskDeadline)         return 'cal-pill cal-pill--red';
    if (ev.status === 'in_progres') return 'cal-pill cal-pill--blue';
    return 'cal-pill cal-pill--grey';
  }
  if (ev.type === 'sarcina_dosar')   return 'cal-pill cal-pill--blue';
  if (ev.type === 'sarcina_interna') return 'cal-pill cal-pill--lblue';
  return 'cal-pill cal-pill--grey';
}

/* ── Tooltip ── */
function EventTooltip({ ev, rect }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ opacity: 0 });

  useEffect(() => {
    if (!rect) return;
    const TIP_W = 320;
    let left = rect.left;
    if (left + TIP_W > window.innerWidth - 12) left = window.innerWidth - TIP_W - 12;
    left = Math.max(12, left);
    const tipH  = ref.current?.offsetHeight || 160;
    const below = rect.bottom + 8;
    const top   = below + tipH < window.innerHeight - 12 ? below : rect.top - 8 - tipH;
    setStyle({ top, left, width: TIP_W, opacity: 1 });
  }, [rect]);

  const isSarcina = ev._isTask || ev.type === 'sarcina_dosar' || ev.type === 'sarcina_interna';

  return (
    <div className="cal-tooltip" ref={ref} style={{ ...style, position: 'fixed' }}>
      <div className="cal-tooltip-title">
        {ev._isTaskDeadline && <span className="cal-tooltip-deadline-tag">Termen limită</span>}
        {ev.text}
      </div>
      {ev.clientName && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Client</span><span>{ev.clientName}</span></div>}
      {ev.procName   && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Procedura</span><span>{ev.procName}</span></div>}
      {ev.denumire   && !ev._isTask && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Dosar</span><span>{ev.denumire}</span></div>}
      {isSarcina && ev.asignatLa?.length > 0 && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Asignat la</span><span>{ev.asignatLa.join(', ')}</span></div>}
      {ev.nota       && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Nota</span><span>{ev.nota}</span></div>}
      {ev.solutie    && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Solutie</span><span>{ev.solutie}</span></div>}
      {ev.descriere  && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Descriere</span><span>{ev.descriere}</span></div>}
      {ev._isTask && ev.termenLimita && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Termen limita</span><span>{fmtDate(ev.termenLimita)}</span></div>}
      {ev._isTask && <div className="cal-tooltip-row"><span className="cal-tooltip-label">Status</span><span>{STATUS_OPTS.find(s => s.value === ev.status)?.label || ev.status}</span></div>}
    </div>
  );
}

/* ── Event pill ── */
function EventPill({ ev, onHover, onLeave, onClickTask }) {
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className={pillClass(ev)}
      onMouseEnter={() => ref.current && onHover(ev, ref.current.getBoundingClientRect())}
      onMouseLeave={onLeave}
      onClick={ev._isTask ? (e) => { e.stopPropagation(); onClickTask(ev.taskId); } : undefined}
      style={ev._isTask ? { cursor: 'pointer' } : undefined}
    >
      <span className="cal-pill-dot" />
      <span className="cal-pill-text">{ev.text}</span>
    </div>
  );
}

/* ── Day cell ── */
function DayCell({ day, events, isCurrentMonth, isToday, onHover, onLeave, onClickTask }) {
  return (
    <div className={['cal-day-cell', !isCurrentMonth ? 'cal-day-cell--outside' : '', isToday ? 'cal-day-cell--today' : ''].join(' ')}>
      <div className={`cal-day-num${isToday ? ' cal-day-num--today' : ''}`}>{day.getDate()}</div>
      <div className="cal-day-events">
        {events.map((ev, i) => (
          <EventPill key={i} ev={ev} onHover={onHover} onLeave={onLeave} onClickTask={onClickTask} />
        ))}
      </div>
    </div>
  );
}


/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

/* ─────────────────────── Page ─────────────────────── */
export default function CalendarPage() {
  const now = new Date();
  const { user } = useAuth();
  const location = useLocation();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [scope, setScope] = useState('all');   // 'all' | 'mine'

  const [proceduri, setProceduri] = useState([]);
  const [tasks, setTasks]         = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [clienti, setClienti]       = useState([]);
  const [allProceduri, setAllProceduri] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tooltip, setTooltip] = useState(null);          // { ev, rect }
  const [modalTask, setModalTask] = useState(undefined);  // undefined=closed, null=new, obj=edit
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toasts, show: showToast } = useToast();

  /* Load on mount */
  useEffect(() => {
    Promise.all([
      listAllProceduri(['dosar_instanta']),
      listTasks(),
      listStaffUsers(),
      listClienti(),
      listAllProceduri([]),
    ]).then(([procData, taskData, usersData, clientiData, allProcData]) => {
      setProceduri(procData.proceduri || []);
      setTasks(taskData.tasks || []);
      setStaffUsers(usersData.users || []);
      setClienti(clientiData.clients || []);
      setAllProceduri(allProcData.proceduri || []);
    }).catch(() => showToast('Eroare la incarcare')).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    // External links (e.g. the daily report email) open a specific task via
    // a query param, same deep-link pattern as ClientiPage/OficiuPage.
    const id = new URLSearchParams(location.search).get('openTaskId');
    if (!id || tasks.length === 0) return;
    const task = tasks.find(t => t.id === Number(id));
    if (!task) return;
    setModalTask(task);
    window.history.replaceState({}, '', location.pathname);
  }, [tasks, location.search, location.pathname]);

  /* Build lookup maps for task display */
  const clientiMap = useMemo(() => {
    const m = {};
    for (const c of clienti) m[c.id] = c.nume;
    return m;
  }, [clienti]);

  const proceduriMap = useMemo(() => {
    const m = {};
    for (const p of allProceduri) m[p.id] = p.data?.denumire || p.data?.numar || p.tip_proc;
    return m;
  }, [allProceduri]);

  const visibleTasks = useMemo(() => {
    // Cancelled tasks (e.g. an [AI] task the coordinator rejected) never
    // belong on the calendar — unlike finished tasks, which stay visible.
    const notCancelled = tasks.filter(t => t.status !== 'anulat');
    if (scope !== 'mine' || !user) return notCancelled;
    return notCancelled.filter(t => (t.asignat_la || []).includes(user.id));
  }, [tasks, scope, user]);

  const byDate = useMemo(() => {
    const base = buildProcEvents(proceduri);
    return mergeTaskEvents(base, visibleTasks, clientiMap, proceduriMap);
  }, [proceduri, visibleTasks, clientiMap, proceduriMap]);

  const calDays  = useMemo(() => getCalendarDays(year, month), [year, month]);
  const todayStr = todayISO();

  /* Navigation */
  const prevMonth = () => { if (month === 0) { setYear(y => y-1); setMonth(11); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y+1); setMonth(0);  } else setMonth(m => m+1); };
  const goToday   = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  /* Tooltip */
  const handleHover = useCallback((ev, rect) => setTooltip({ ev, rect }), []);
  const handleLeave = useCallback(() => setTooltip(null), []);

  /* Click task pill → open edit */
  const handleClickTask = useCallback((taskId) => {
    const t = tasks.find(t => t.id === taskId);
    if (t) { setTooltip(null); setModalTask(t); }
  }, [tasks]);

  /* Save */
  const handleSave = async (form, pendingFiles = []) => {
    setSaving(true);
    try {
      let taskId;
      if (modalTask?.id) {
        const res = await updateTask(modalTask.id, form);
        setTasks(prev => prev.map(t => t.id === modalTask.id ? res.task : t));
        taskId = modalTask.id;
        showToast('✓ Task actualizat');
      } else {
        const res = await createTask(form);
        setTasks(prev => [res.task, ...prev]);
        taskId = res.task.id;
        showToast('✓ Task creat');
      }
      for (const p of pendingFiles) {
        const fd = new FormData();
        fd.append('file', p.file);
        fd.append('denumire', p.desc);
        await uploadTaskFile(taskId, fd).catch(() => {});
      }
      setModalTask(undefined);
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  /* Delete */
  const handleDelete = async () => {
    if (!modalTask?.id || !window.confirm('Sigur vrei sa stergi acest task?')) return;
    setDeleting(true);
    try {
      await deleteTask(modalTask.id);
      setTasks(prev => prev.filter(t => t.id !== modalTask.id));
      setModalTask(undefined);
      showToast('Task sters');
    } catch {
      showToast('Eroare la stergere');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          <h1 className="page-title">Calendar</h1>
        </div>
        <div className="topbar-right">
          <div className="cal-scope-toggle">
            <button
              type="button"
              className={`cal-scope-btn${scope === 'mine' ? ' cal-scope-btn--active' : ''}`}
              onClick={() => setScope('mine')}
            >
              Task-urile mele
            </button>
            <button
              type="button"
              className={`cal-scope-btn${scope === 'all' ? ' cal-scope-btn--active' : ''}`}
              onClick={() => setScope('all')}
            >
              Toate taskurile
            </button>
          </div>
          <button className="btn btn-primary btn-sm" type="button" onClick={() => { setTooltip(null); setModalTask(null); }}>
            + Task nou
          </button>
        </div>
      </div>

      <div className="cal-wrap">
        {/* Nav */}
        <div className="cal-nav">
          <div className="cal-nav-arrows">
            <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <button className="cal-nav-btn" onClick={nextMonth}>›</button>
            <button className="cal-nav-btn cal-nav-today" onClick={goToday}>Azi</button>
          </div>
          <div className="cal-nav-title">{MONTHS_RO[month].toUpperCase()} {year}</div>
          <div />
        </div>

        {/* Grid */}
        <div className="cal-grid-wrap">
          {loading && <div className="cal-loading">Se incarca...</div>}
          <div className="cal-grid">
            {DAYS_RO.map(d => <div key={d} className="cal-day-header">{d}</div>)}
            {calDays.map(day => {
              const iso = toISO(day);
              return (
                <DayCell
                  key={iso}
                  day={day}
                  events={byDate[iso] || []}
                  isCurrentMonth={day.getMonth() === month}
                  isToday={iso === todayStr}
                  onHover={handleHover}
                  onLeave={handleLeave}
                  onClickTask={handleClickTask}
                />
              );
            })}
          </div>
        </div>
      </div>

      {tooltip && <EventTooltip ev={tooltip.ev} rect={tooltip.rect} />}

      {modalTask !== undefined && (
        <TaskModal
          task={modalTask}
          staffUsers={staffUsers}
          clienti={clienti}
          allProceduri={allProceduri}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModalTask(undefined)}
          saving={saving}
          deleting={deleting}
        />
      )}

      <div className="toast-wrap">
        {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
      </div>
    </main>
  );
}
